import React, { createContext, useContext, useState, useCallback, ReactNode, useEffect } from 'react';
import { tourApi } from '../repositories/tour';

const tourMinimizedKey = "cadence_tour_minimized";
const tourStepKey = "cadence_tour_step";
const tourSectionKey = "cadence_tour_section";

export type TourSection = "dashboard" | "dsps" | "roster" | "assets" | "createPitch";

interface TourContextType {
  isMinimized: boolean;
  setIsMinimized: (value: boolean) => void;
  // True while the product tour may be on screen (or about to auto-start), so
  // pages can hold back their own modals instead of stacking them under it.
  isTourRunning: boolean;
  isTourCompleted: () => boolean;
  markTourCompleted: () => Promise<void>;
  markTourNotCompleted: () => void;
  getCurrentTourSection: () => TourSection;
  setCurrentTourSection: (section: TourSection) => void;
  getCurrentTourStep: () => number;
  setCurrentTourStep: (step: number) => void;
  minimizeTour: (step: number, section: TourSection) => void;
  clearTourProgress: () => void;
  tourLoading: boolean;
  fetchTourStatus: () => Promise<void>;
}

const TourContext = createContext<TourContextType | null>(null);

interface TourProviderProps {
  children: ReactNode;
  userId?: number;
  userRole?: string;
}

export const TourProvider: React.FC<TourProviderProps> = ({ children, userId, userRole }) => {
  const [isMinimized, setIsMinimizedState] = useState(() => {
    return localStorage.getItem(tourMinimizedKey) === "true";
  });

  const [tourCompleted, setTourCompleted] = useState<boolean>(false);
  const [tourLoading, setTourLoading] = useState<boolean>(true);

  const setIsMinimized = useCallback((value: boolean) => {
    setIsMinimizedState(value);
    if (value) {
      localStorage.setItem(tourMinimizedKey, "true");
    } else {
      localStorage.removeItem(tourMinimizedKey);
    }
  }, []);

  const fetchTourStatus = useCallback(async () => {
    if (!userId || userRole !== "basic") {
      setTourLoading(false);
      return;
    }

    try {
      setTourLoading(true);
      const response = await tourApi.getTourLog(userId);

      if (response?.data?.is_completed !== undefined) {
        setTourCompleted(response.data.is_completed);
      }
    } catch (error: any) {
      if (error?.response?.status === 404) {
        setTourCompleted(false);
      } else {
        console.error("Error fetching tour status:", error);
        setTourCompleted(false);
      }
    } finally {
      setTourLoading(false);
    }
  }, [userId, userRole]);

  useEffect(() => {
    fetchTourStatus();
  }, [fetchTourStatus]);

  const isTourCompleted = useCallback(() => {
    return tourCompleted;
  }, [tourCompleted]);

  const markTourCompleted = useCallback(async () => {
    if (!userId || userRole !== "basic") return;

    try {
      await tourApi.updateTourLog(userId, { is_completed: true });

      setTourCompleted(true);

      // Clear resume data
      localStorage.removeItem(tourMinimizedKey);
      localStorage.removeItem(tourStepKey);
      localStorage.removeItem(tourSectionKey);
      setIsMinimizedState(false);
    } catch (error: any) {
      if (error?.response?.status === 404) {
        try {
          await tourApi.createTourLog(userId, { is_completed: true });
          setTourCompleted(true);

          localStorage.removeItem(tourMinimizedKey);
          localStorage.removeItem(tourStepKey);
          localStorage.removeItem(tourSectionKey);
          setIsMinimizedState(false);
        } catch (createError) {
          console.error("Error creating tour log:", createError);
        }
      } else {
        console.error("Error marking tour as completed:", error);
      }
    }
  }, [userId, userRole]);

  const markTourNotCompleted = useCallback(() => {
    setTourCompleted(false);
  }, []);

  const getCurrentTourSection = useCallback((): TourSection => {
    return (localStorage.getItem(tourSectionKey) as TourSection) || "dashboard";
  }, []);

  const setCurrentTourSection = useCallback((section: TourSection) => {
    localStorage.setItem(tourSectionKey, section);
  }, []);

  const getCurrentTourStep = useCallback(() => {
    return parseInt(localStorage.getItem(tourStepKey) || "0", 10);
  }, []);

  const setCurrentTourStep = useCallback((step: number) => {
    localStorage.setItem(tourStepKey, step.toString());
  }, []);

  const minimizeTour = useCallback((step: number, section: TourSection) => {
    localStorage.setItem(tourMinimizedKey, "true");
    localStorage.setItem(tourStepKey, step.toString());
    localStorage.setItem(tourSectionKey, section);
    setIsMinimizedState(true);
  }, []);

  const clearTourProgress = useCallback(() => {
    localStorage.removeItem(tourMinimizedKey);
    localStorage.removeItem(tourStepKey);
    setIsMinimizedState(false);
  }, []);

  // The tour only runs for basic users. While the status is still loading we
  // don't know yet, so treat it as running rather than flash a modal under it.
  const isTourRunning =
    userRole === "basic" && (tourLoading || (!tourCompleted && !isMinimized));

  return (
    <TourContext.Provider
      value={{
        isMinimized,
        setIsMinimized,
        isTourRunning,
        isTourCompleted,
        markTourCompleted,
        markTourNotCompleted,
        getCurrentTourSection,
        setCurrentTourSection,
        getCurrentTourStep,
        setCurrentTourStep,
        minimizeTour,
        clearTourProgress,
        tourLoading,
        fetchTourStatus,
      }}
    >
      {children}
    </TourContext.Provider>
  );
};

export const useTourContext = () => {
  const context = useContext(TourContext);
  if (!context) {
    throw new Error('useTourContext must be used within a TourProvider');
  }
  return context;
};

// Safe outside a TourProvider (e.g. page tests): no provider means no tour.
export const useIsTourRunning = () =>
  useContext(TourContext)?.isTourRunning ?? false;
